import { InsuranceCoverageMonitorStatus } from "@wingspanhq/benefits/dist/src/lib/interfaces/insuranceCoverageMonitor";
import {
  useWSSnackbar,
  WSPageToolbarAction,
  WSPoster
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { useMutationDownloadInsuranceCoverageFile } from "../../../../../../query/insuranceCoverageMonitor/mutations/useMutationDownloadInsuranceCoverageFile";
import type { insuranceMonitorApiSpec } from "@wingspanhq/wingspan-api-types";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { InsuranceCoverageDetails } from "../../components/InsuranceCoverageDetails";
import { InsuranceCoverageDetailsWidgetContainer } from "../../components/InsuranceCoverageDetailsWidgetContainer";
import { selectorPolicyTypesFromSettings } from "../../selectors/selectorPolicyTypesFromSettings";
import { getInsuranceCoveragePayeeRequirementStatus } from "../helpers/getInsuranceCoveragePayeeRequirementStatus";
import { getInsuranceCoverageProgressSteps } from "../helpers/getInsuranceCoverageProgressSteps";
import { getInsuranceCoverageRequirementName } from "../helpers/getInsuranceCoverageRequirementName";
import { INSURANCE_COVERAGE_REQUIREMENT_EVENT } from "../utils";
import { selectorInsuranceCoverageWarnings } from "../../helpers/insuranceWarnings";

type InsuranceCoverageMonitor =
  insuranceMonitorApiSpec.InsuranceCoverageMonitor;

export const usePayeeInsuranceCoverageConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  InsuranceCoverageMonitor | undefined
> => {
  const [downloadInsuranceCoverageFile, downloadInsuranceCoverageFileMeta] =
    useMutationDownloadInsuranceCoverageFile();
  const { openSnackbar } = useWSSnackbar();

  const getDownloadInsuranceCoverageFileAction = (
    requirement: IRequirementResponse,
    insuranceCoverageMonitor?: InsuranceCoverageMonitor
  ) => {
    if (
      (insuranceCoverageMonitor?.status === "ForceMatched" ||
        insuranceCoverageMonitor?.status === "Complete") &&
      insuranceCoverageMonitor?.insuranceCoverage?.sourceType ===
        "InsuranceImport"
    ) {
      return async () => {
        await downloadInsuranceCoverageFile(
          {
            insuranceCoverageMonitorId:
              insuranceCoverageMonitor.insuranceCoverageMonitorId!,
            fileName: `Policy--${
              insuranceCoverageMonitor.insuranceCoverage?.policyNumber ||
              insuranceCoverageMonitor.insuranceCoverageMonitorId
            }`
          },
          {
            onSuccess: () => {
              openSnackbar({
                message: "File downloaded successfully",
                type: "success"
              });
              callback(
                INSURANCE_COVERAGE_REQUIREMENT_EVENT.DownloadAsPayer,
                requirement
              );
            },
            onError: () => {
              openSnackbar({
                message: "Download failed",
                type: "warning"
              });
            }
          }
        );
      };
    }

    return undefined;
  };

  return {
    isActionRequired: () => {
      return false;
    },
    requirementName: (requirement, _context, insuranceCoverage) =>
      getInsuranceCoverageRequirementName(
        insuranceCoverage,
        requirement.name || "Insurance Coverage"
      ),

    requirementInstructions:
      (requirement, _context, insuranceCoverage) => (instructions: string) => {
        if (
          insuranceCoverage?.status === InsuranceCoverageMonitorStatus.Complete
        ) {
          return "Your insurance coverage has been submitted successfully.";
        }
        return (
          instructions || "" || "Please provide proof of insurance coverage."
        );
      },

    requirementDetails: (requirement, context, insuranceCoverage) => {
      const downloadAction = getDownloadInsuranceCoverageFileAction(
        requirement,
        insuranceCoverage
      );

      if (requirement.payerOverrideStatus === "Incomplete") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="The policy submitted by the contractor was rejected by you."
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage!}
            pill={{
              theme: "error",
              text: "Rejected",
              icon: true
            }}
          />
        );
      } else if (insuranceCoverage?.status === "Complete") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="A policy from the contractor’s resources matched this requirement."
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage!}
            onDownloadClick={downloadAction}
            pill={{
              theme: "success",
              text: "Passed",
              icon: true
            }}
          />
        );
      } else if (insuranceCoverage?.status === "ForceMatched") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description={
              selectorInsuranceCoverageWarnings(insuranceCoverage.insuranceCoverage)
                .length > 0
                ? "The contractor submitted a policy with a check that needs your decision"
                : "The contractor submitted a partially matched policy for your approval"
            }
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage}
            onDownloadClick={downloadAction}
            pill={{
              theme: "warning",
              text: "Partial match",
              icon: true
            }}
          />
        );
      } else if (
        requirement.payerOverrideStatus == "Complete" &&
        (insuranceCoverage?.status === "Pending" ||
          insuranceCoverage?.status === "Monitoring")
      ) {
        return {
          header: {
            label: {
              text: requirement.name
            },
            value: {
              pill: {
                theme: "success",
                text: "Approved by you",
                icon: "block"
              }
            }
          },
          data: [],
          children: (
            <WSPoster
              title="Policy requirement marked complete by you"
              description="Contractor's policy requirement has been marked complete by you"
              type="documents"
            />
          )
        };
      } else {
        return {
          header: {
            label: {
              text: requirement.name
            },
            value: {
              pill: {
                theme: "neutral",
                text: "Not started",
                icon: "block"
              }
            }
          },
          data: [],
          children: (
            <WSPoster
              title="Awaiting proof of insurance"
              description="Waiting for contractor to provide their proof of insurance."
              type="documents"
            />
          )
        };
      }
    },
    requirementDetailsProgressWidget: (
      _requirement,
      _context,
      insuranceCoverage
    ) => {
      const steps = getInsuranceCoverageProgressSteps(insuranceCoverage);
      return steps;
    },

    requirementDetailsWidgets: (requirement, context, insuranceCoverage) => {
      return insuranceCoverage ? (
        <InsuranceCoverageDetailsWidgetContainer
          insuranceCoverage={insuranceCoverage}
          context={RequirementContextType.Payee}
          isInitialLoading={false}
        />
      ) : null;
    },

    toolbarActions: (requirement, context, monitor) => {
      const downloadAction = getDownloadInsuranceCoverageFileAction(
        requirement,
        monitor
      );

      const actions: WSPageToolbarAction[] = [
        {
          buttonKind: "Secondary",
          label: "View policy",
          onAsyncClick: downloadAction,
          visible: !!downloadAction
        }
      ];

      return actions;
    },

    nameCell: (requirement, _context, insuranceCoverage) => ({
      text: getInsuranceCoverageRequirementName(
        insuranceCoverage,
        requirement.name || "Insurance Coverage"
      )
    }),

    typeCell: (requirement, context, insuranceCoverage) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType,
        policyType: selectorPolicyTypesFromSettings(insuranceCoverage?.settings)
      }),

    statusCell: (requirement, context, insuranceCoverage) => {
      return getInsuranceCoveragePayeeRequirementStatus(
        requirement,
        insuranceCoverage
      );
    },
    engagementsCell: getRequirementEngagementsTableCellProps
  };
};
